import SysAdminTenantFormComponent from '../components/sysadmin-tenant-form.component';
import { TenantUpdate } from '../types/tenant.types';
import { useTenantGet, useTenantUpdate } from '../hooks/use-tenant.hook';
import { message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  extractCreateError,
  extractValidationError,
} from '../../../../../services/extract-api-error.service';
import { useEffect, useState, useCallback } from 'react';
import HeaderRow from '../../../../common/HeaderRow';
import { ValidationError } from '../../../../../types/api-response.types';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { DefaultValues } from 'react-hook-form';
import dayjs from 'dayjs';
import { WithSysAdminPermissionCheck } from '../../../../auth/WithSysAdminPermissionCheck';
import PageTitle from '@components/common/PageTitle';

interface FormSubmitHandler {
  (data: TenantUpdate, resetForm: () => void): void;
}

const SysAdminTenantUpdateContainer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [formDefaultValues, setFormDefaultValues] = useState<DefaultValues<TenantUpdate>>({});
  const { data, isLoading, error } = useTenantGet(id ?? '');
  const { mutateAsync: updateTenant, isPending } = useTenantUpdate();

  const extractFormDefaultValues = useCallback(async () => {
    if (!data?.data) return;
    const { data: tenantData } = data;

    setFormDefaultValues({
      name: tenantData.name ?? undefined,
      addressLine1: tenantData.addressLine1 ?? undefined,
      addressLine2: tenantData.addressLine2 ?? undefined,
      city: tenantData.city ?? undefined,
      state: tenantData.state ?? undefined,
      zipCode: tenantData.zipCode ?? undefined,
      countryId: tenantData.country?.id ?? undefined,
      region: tenantData.region ?? undefined,
      primaryEmail: tenantData.primaryEmail ?? undefined,
      secondaryEmail: tenantData.secondaryEmail ?? undefined,
      primaryPhone: tenantData.primaryPhone ?? undefined,
      secondaryPhone: tenantData.secondaryPhone ?? undefined,
      defaultLanguage: tenantData.defaultLanguage ?? undefined,
      defaultCurrency: tenantData.defaultCurrency ?? undefined,
      parentTenantId: tenantData.parentTenantId ?? undefined,
      brandingColors: tenantData.brandingColors ?? undefined,
      billingPlanId: tenantData.billingPlanId ?? undefined,
      supportPlanId: tenantData.supportPlanId ?? undefined,
      contractStartDate: tenantData.contractStartDate
        ? dayjs(tenantData.contractStartDate)
        : undefined,
      contractEndDate: tenantData.contractEndDate ? dayjs(tenantData.contractEndDate) : undefined,
      notes: tenantData.notes ?? undefined,
    });
  }, [data]);

  useEffect(() => {
    let isMounted = true; // to prevent memory leaks
    extractFormDefaultValues();
    return () => {
      isMounted = false;
    };
  }, [extractFormDefaultValues]);

  const onFormSubmit: FormSubmitHandler = useCallback(
    async (data, resetForm) => {
      if (!id) return;
      // Reset validation errors on new submission
      setValidationErrors([]);
      try {
        await updateTenant({ id, data });
        message.success(t('crud.update.success', { entity: t('tenants.title') }));
        navigate(-1);
      } catch (error: any) {
        const errorFields = extractValidationError(error);
        setValidationErrors(errorFields);
        const errorMessage = extractCreateError(
          error,
          t('crud.update.error', { entity: t('tenants.title') })
        );
        message.error(errorMessage);
      }
    },
    [id, updateTenant, t, navigate]
  );

  if (!id) {
    return (
      <ErrorFallback
        details={t('crud.details.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('common.invalidIdProvided'))}
        resetErrorBoundary={() => {}}
      />
    );
  }

  if (error) {
    return (
      <>
        <PageTitle title={t('crud.update.title', { entity: t('tenants.title') })} />
        <HeaderRow
          title={t('crud.update.title', { entity: t('tenants.title') })}
          showBackButton={true}
        />
        <ErrorFallback
          details={t('crud.details.errorLoadingData')}
          icon={<ExclamationCircleOutlined />}
          error={error}
          resetErrorBoundary={() => {}}
        />
      </>
    );
  }

  return (
    <>
      <PageTitle title={t('crud.update.title', { entity: t('tenants.title') })} />
      <HeaderRow
        title={t('crud.update.title', { entity: t('tenants.title') })}
        showBackButton={true}
        isSysAdmin={true}
      />
      <div className="row mt-3">
        <SysAdminTenantFormComponent
          onFormSubmit={onFormSubmit}
          isLoading={isPending || isLoading}
          defaultValues={formDefaultValues}
          columns={3}
          validationErrors={validationErrors}
        />
      </div>
    </>
  );
};

export default WithSysAdminPermissionCheck(SysAdminTenantUpdateContainer, 'tenant.update');
